import React, { useState } from 'react';
import { X } from 'lucide-react';
import { teamService, CreateTeamRequest } from '../../../services/teams/teamService';
import { useQueryClient } from '@tanstack/react-query';

interface CreateTeamFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateTeamForm: React.FC<CreateTeamFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [division, setDivision] = useState('');
  const [gender, setGender] = useState<'men' | 'women' | 'mixed' | ''>('');
  const [sport, setSport] = useState('rowing');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Team name is required');
      return;
    }

    setLoading(true);

    try {
      const payload: CreateTeamRequest = {
        name: trimmedName,
        sport: sport || 'rowing',
      };

      if (division.trim()) {
        payload.division = division.trim();
      }

      if (gender) {
        payload.gender = gender;
      }

      // eslint-disable-next-line no-console
      console.log('[CreateTeamForm] Submitting team creation:', payload);
      const result = await teamService.createTeam(payload);
      // eslint-disable-next-line no-console
      console.log('[CreateTeamForm] Team creation result:', result);

      if (result.error) {
        console.error('[CreateTeamForm] Error creating team:', result.error);
        setError(result.error.message || 'Failed to create team');
        setLoading(false);
        return;
      }

      if (!result.data) {
        console.error('[CreateTeamForm] No data returned from team creation');
        setError('Failed to create team: No data returned');
        setLoading(false);
        return;
      }

      // Invalidate and refetch team queries
      await queryClient.invalidateQueries({ queryKey: ['current-team'] });
      await queryClient.invalidateQueries({ queryKey: ['coach-teams'] });
      // Also refetch immediately so the UI updates right away
      await queryClient.refetchQueries({ queryKey: ['current-team'] });
      await queryClient.refetchQueries({ queryKey: ['coach-teams'] });

      // Reset form
      setName('');
      setDivision('');
      setGender('');
      setSport('rowing');

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('[CreateTeamForm] Unexpected error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Create Team</h3>
            <p className="text-sm text-gray-500">Create your first team to get started.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Close create team dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6">
          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="team-name">
              Team name <span className="text-red-500">*</span>
            </label>
            <input
              id="team-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent/50"
              placeholder="e.g., Varsity Men's Team"
              autoFocus
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="team-division">
              Division (optional)
            </label>
            <select
              id="team-division"
              value={division}
              onChange={(e) => setDivision(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent/50"
            >
              <option value="">Select division</option>
              <option value="D1">D1</option>
              <option value="D2">D2</option>
              <option value="D3">D3</option>
              <option value="NAIA">NAIA</option>
              <option value="NJCAA">NJCAA</option>
              <option value="Club">Club</option>
              <option value="High School">High School</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="team-gender">
              Gender (optional)
            </label>
            <select
              id="team-gender"
              value={gender}
              onChange={(e) => setGender(e.target.value as 'men' | 'women' | 'mixed' | '')}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent/50"
            >
              <option value="">Select gender</option>
              <option value="men">Men</option>
              <option value="women">Women</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="team-sport">
              Sport
            </label>
            <input
              id="team-sport"
              type="text"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent/50"
              placeholder="rowing"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:bg-accent-hover disabled:opacity-60"
            >
              {loading ? 'Creating…' : 'Create Team'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTeamForm;
